import { MouseEvent, ReactElement, RefObject, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { createPortal } from 'react-dom';
import {
  BarChart as RBarChart,
  Bar,
  BarProps,
  CartesianGrid,
  LegendType,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
  XAxisProps as RXAxisProps,
  YAxisProps as RYAxisProps,
} from 'recharts';
import useWindowDimensions from 'ui_system/utilities/useWindowDimensions';
import {
  ChartWrapper,
  OverflowContainer,
  Watermark,
} from '../chart_styles/styles';
import { colors, fontSizes, shadows, spacing } from '../utils';

const defaultTooltipWidth = 140;

/* TYPES */
interface XAxisProps extends RXAxisProps {
  label?: string | number | ReactElement | object | Record<string, any>;
}

interface YAxisProps extends RYAxisProps {
  label?: string | number | ReactElement | object | Record<string, any>;
}

interface StickyYAxisWrapperProps {
  height: number;
}

interface StickyYAxisProps {
  label: string;
  maxHeight: number;
}

interface TooltipWrapperProps {
  tooltipWidth: number;
  x: number;
  y: number;
}

interface TooltipValueProps {
  smallMargin?: boolean;
}

interface TooltipBenchmarkDiffProps {
  diff: number;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  benchmark?: string | number;
  coordinates: [x: number, y: number] | null;
  dimensions: {
    height?: number;
    width?: number;
  };
}

interface StickyXAxisWrapperProps {
  maxWidth?: number;
  minHeight?: number;
}

type BenchmarkProps = Pick<
  BarProps,
  'barSize' | 'dataKey' | 'fill' | 'legendType' | 'maxBarSize' | 'name'
>;

interface BarChartProps {
  barCategoryGap?: number;
  barColor?: string;
  barGap?: number;
  barName?: string;
  barSize?: number;
  benchmarkProps?: BenchmarkProps;
  data?: Record<string, any>[];
  dataKey: string;
  disableAnimations?: boolean;
  downloadRef?: RefObject<any>;
  height?: number;
  includeWatermark?: boolean;
  layout?: 'horizontal' | 'vertical';
  legendType?: LegendType;
  maxBarSize?: number;
  maxHeight: number;
  maxWidth: number;
  minWidth: number;
  stickyAxes?: boolean;
  tooltipFormatter(value: number): [formattedValue: number, label?: string];
  unit?: string;
  xAxisProps?: XAxisProps;
  yAxisProps?: YAxisProps;
}

/* STYLES */

const StickyYAxisWrapper = styled.div<StickyYAxisWrapperProps>`
  color: ${colors.text.secondary};
  font-size: ${fontSizes.small};
  height: ${(props) => props.height}px;
  left: 0px;
  top: 0px;
  position: sticky;
`;
StickyYAxisWrapper.displayName = 'StickyYAxisWrapper';

const StickyYAxis = ({ label, maxHeight }: StickyYAxisProps) => {
  return (
    <StickyYAxisWrapper height={maxHeight}>
      <svg height={maxHeight} viewBox={`0 0 50 ${maxHeight}`} width="50">
        <text
          dominantBaseline="middle"
          fill={colors.text.secondary}
          fontSize={fontSizes.small}
          textAnchor="middle"
          transform="rotate(-90)"
          x={`-${maxHeight / 2}`}
          y="25"
        >
          {label}
        </text>
      </svg>
    </StickyYAxisWrapper>
  );
};
StickyYAxis.displayName = 'StickyYAxis';

const TooltipWrapper = styled.div<TooltipWrapperProps>`
  background: ${colors.white};
  box-shadow: ${shadows.primary};
  font-family: Soleil;
  left: 0;
  padding: ${spacing.medium};
  padding-right: ${spacing.none};
  position: absolute;
  top: 0;
  transform: translate3d(${(props) => props.x}px, ${(props) => props.y}px, 0);
  transition: transform 400ms ease;
  width: ${(props) => props.tooltipWidth}px;
  z-index: 100;
`;
TooltipWrapper.displayName = 'TooltipWrapper';

const TooltipLabelValueGroup = styled.div`
  margin-top: ${spacing.xxsmall};
`;
TooltipLabelValueGroup.displayName = 'TooltipLabelValueGroup';

const TooltipLabel = styled.p`
  color: ${colors.text.secondary};
  font-family: Soleil;
  font-size: 11px;
  font-weight: 400;
  margin: 0;
  margin-bottom: ${spacing.xxsmall};
  text-transform: uppercase;
`;
TooltipLabel.displayName = 'TooltipLabel';

const TooltipValue = styled.p<TooltipValueProps>`
  color: ${colors.text.primary};
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  margin-bottom: ${(props) =>
    props.smallMargin ? spacing.small : spacing.medium};
`;
TooltipValue.displayName = 'TooltipValue';

const TooltipValueAndBenchmark = styled.div`
  display: flex;
  justify-content: space-between;
`;
TooltipValueAndBenchmark.displayName = 'TooltipValueAndBenchmark';

const TooltipBenchmarkData = styled.div`
  background: ${colors.background.light};
  padding: ${spacing.xsmall};
`;
TooltipBenchmarkData.displayName = 'TooltipBenchmarkData';

const TooltipBenchmarkLabel = styled.div`
  color: ${colors.text.icon};
  font-size: 9px;
  padding-bottom: ${spacing.xxsmall};
  white-space: nowrap;
`;
TooltipBenchmarkLabel.displayName = 'TooltipBenchmarkLabel';

const TooltipBenchmarkRow = styled.div`
  display: flex;
  justify-content: space-between;
`;
TooltipBenchmarkRow.displayName = 'TooltipBenchmarkRow';

const TooltipBenchmarkValue = styled.div`
  color: ${colors.text.primary};
  font-size: 13px;
  padding-right: ${spacing.small};
`;
TooltipBenchmarkValue.displayName = 'TooltipBenchmarkValue';

const getDiffColor = (diff: number) => {
  if (diff === 0) {
    return colors.data.secondary;
  }
  if (diff < 0) {
    return colors.data.negative;
  }
  return colors.data.positive;
};

const TooltipBenchmarkDiff = styled.div<TooltipBenchmarkDiffProps>`
  color: ${(props) => getDiffColor(props.diff)};
`;
TooltipBenchmarkDiff.displayName = 'TooltipBenchmarkDiff';

const formatDiff = (diff: number) => {
  const absDiff = Math.abs(diff);
  const formattedValue = `${Math.round(absDiff)}`;
  return `${diff < 0 ? '-' : '+'}${formattedValue}%`;
};

const defaultTooltipFormatter = (value: number) => [value];

const CustomTooltip = (props: CustomTooltipProps) => {
  const {
    active,
    benchmark,
    coordinates,
    dimensions,
    formatter,
    label,
    payload,
  } = props;
  const target = document.body;
  if (!active || !coordinates || !payload) return null;

  let tooltipWidth = defaultTooltipWidth;
  // If there's benchmark data, the payload is returned
  // in render order: [Bar/compare_to_value, Bar/value]
  const load = benchmark ? payload[1] : payload[0];
  const format = formatter || defaultTooltipFormatter;
  // @ts-ignore
  const formattedValue = format(load.value)[0];
  const benchmarkLoad = benchmark && payload[0];
  const formattedBenchmarkValue =
    // @ts-ignore
    benchmarkLoad && format(benchmarkLoad.value)[0];
  const diff = benchmarkLoad
    ? Math.round(load.value || 0) - Math.round(benchmarkLoad.value || 0)
    : 0;
  const formattedDiff = formatDiff(diff);

  if (label && load.name) {
    tooltipWidth = 200;
  }
  const { width = 0 } = dimensions;
  let x = coordinates[0] + 10;
  const y = coordinates[1];
  if (x + tooltipWidth >= width) {
    x -= tooltipWidth + 20;
  }

  const TooltipPortal = (
    <TooltipWrapper tooltipWidth={tooltipWidth} x={x} y={y}>
      {label && load.name && (
        <>
          <TooltipLabel>{load.name}</TooltipLabel>
          <TooltipValue smallMargin={!!benchmark}>{label}</TooltipValue>
        </>
      )}
      <TooltipValueAndBenchmark>
        <TooltipLabelValueGroup>
          <TooltipLabel>{load.unit}</TooltipLabel>
          <TooltipValue>{formattedValue}</TooltipValue>
        </TooltipLabelValueGroup>
        {benchmark && (
          <TooltipBenchmarkData>
            <TooltipBenchmarkLabel>{benchmark}</TooltipBenchmarkLabel>
            <TooltipBenchmarkRow>
              <TooltipBenchmarkValue>
                {formattedBenchmarkValue}
              </TooltipBenchmarkValue>
              <TooltipBenchmarkDiff diff={diff}>
                {formattedDiff}
              </TooltipBenchmarkDiff>
            </TooltipBenchmarkRow>
          </TooltipBenchmarkData>
        )}
      </TooltipValueAndBenchmark>
    </TooltipWrapper>
  );

  return createPortal(TooltipPortal, target);
};
CustomTooltip.displayName = 'CustomTooltip';

const StickyXAxisWrapper = styled.div<StickyXAxisWrapperProps>`
  width: 100%;
  ${(props) => props.maxWidth && `max-width: ${props.maxWidth}px`};
  ${(props) => props.minHeight && `min-height: ${props.minHeight}px`};
`;
StickyXAxisWrapper.displayName = 'StickyXAxisWrapper';

export const BarChart = ({
  barCategoryGap = 12,
  barColor = colors.data.primary,
  barGap = 12,
  barName = '',
  barSize,
  benchmarkProps,
  data = [],
  dataKey,
  disableAnimations = false,
  downloadRef,
  height,
  includeWatermark = false,
  layout = 'vertical',
  legendType = 'none',
  maxBarSize = 60,
  maxHeight,
  maxWidth,
  minWidth,
  stickyAxes = false,
  tooltipFormatter,
  unit,
  xAxisProps = {},
  yAxisProps = {},
}: BarChartProps) => {
  const dimensions = useWindowDimensions();
  const containerRef = useRef(null);
  const [mouseCoordinates, setMouseCoordinates] = useState<
    [x: number, y: number] | null
  >(null);
  let maxChartWidth = maxWidth;
  let maxChartHeight = maxHeight;
  let minChartWidth = minWidth;
  const chartMargins = { bottom: 35, left: 20, top: 10, right: 50 };
  let hideXAxis = !!xAxisProps.hide;
  let yAxisLabel = yAxisProps?.label;

  if (stickyAxes) {
    hideXAxis = true;
    yAxisLabel = undefined;
    chartMargins.bottom = 0;
    chartMargins.left = 0;
    maxChartWidth -= 50;
    maxChartHeight -= 70;
    minChartWidth -= 50;
  }

  const onMouseMove = (_nextState: any, e: MouseEvent): void => {
    if (e.clientX && e.clientY) {
      setMouseCoordinates([e.clientX, e.clientY]);
    }
  };

  return (
    <>
      {stickyAxes && (
        <StickyYAxis
          label={(yAxisProps?.label as Record<string, any>)?.value}
          maxHeight={maxHeight}
        />
      )}
      <ChartWrapper maxWidth={maxChartWidth} minWidth={minChartWidth}>
        <OverflowContainer
          maxHeight={maxChartHeight}
          maxWidth={maxChartWidth}
          minWidth={minChartWidth}
        >
          {includeWatermark && <Watermark />}
          <ResponsiveContainer
            height={height}
            minWidth={minChartWidth}
            ref={containerRef}
            width="100%"
          >
            <RBarChart
              barCategoryGap={barCategoryGap}
              barGap={barGap}
              barSize={barSize}
              data={data}
              height={height}
              layout={layout}
              margin={chartMargins}
              onMouseMove={onMouseMove}
              ref={downloadRef}
            >
              <CartesianGrid
                horizontal={layout === 'horizontal'}
                stroke={colors.graphLine}
                vertical={layout === 'vertical'}
              />
              <XAxis {...(xAxisProps as any)} hide={hideXAxis} />
              <YAxis {...(yAxisProps as any)} label={yAxisLabel} />
              <Tooltip
                allowEscapeViewBox={{ x: true, y: true }}
                content={
                  <CustomTooltip
                    benchmark={benchmarkProps?.name}
                    coordinates={mouseCoordinates}
                    dimensions={dimensions}
                  />
                }
                cursor={false}
                // @ts-ignore
                formatter={tooltipFormatter}
                useTranslate3d
              />
              {benchmarkProps && (
                <Bar
                  {...benchmarkProps}
                  isAnimationActive={!disableAnimations}
                  stackId="benchmark"
                />
              )}
              <Bar
                barSize={barSize}
                dataKey={dataKey}
                fill={barColor}
                isAnimationActive={!disableAnimations}
                legendType={legendType}
                maxBarSize={maxBarSize}
                name={barName}
                stackId="barchart"
                unit={unit}
              />
            </RBarChart>
          </ResponsiveContainer>
        </OverflowContainer>
        {stickyAxes && (
          <StickyXAxisWrapper maxWidth={maxChartWidth}>
            <ResponsiveContainer
              height={70}
              minWidth={minChartWidth}
              width="100%"
            >
              <RBarChart
                data={data}
                layout={layout}
                margin={{ bottom: 35, left: 0, right: 50, top: 0 }}
              >
                <XAxis {...(xAxisProps as any)} hide={false} />
                <YAxis
                  {...(yAxisProps as any)}
                  label={undefined}
                  tick={false}
                />
                <Bar
                  dataKey={dataKey}
                  fill="transparent"
                  isAnimationActive={false}
                  legendType="none"
                  name={barName}
                />
              </RBarChart>
            </ResponsiveContainer>
          </StickyXAxisWrapper>
        )}
      </ChartWrapper>
    </>
  );
};

BarChart.displayName = 'BarChart';
